import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import { trackPhoneCall, trackQuoteRequest } from "@/utils/gtag";
import Footer from "@/components/feature/Footer";
import BeforeAfter from "@/pages/home/components/BeforeAfter";
import JsonLd from "@/components/base/JsonLd";
import { featuredCases } from "@/mocks/portfolio";

const caseDescriptions: Record<string, string> = {
  "case-school-zone-buk":
    "부산광역시 북구 어린이보호구역 내 횡단보도 및 도로 표시를 선명한 노란색 도로용 페인트로 도색 정비한 공사입니다. 기존 흰색 표시가 마모되어 시인성이 저하된 구간을 노란색으로 전면 재도색하였으며, 어린이 통학 안전을 강화하고 운전자의 서행 준수를 유도하는 효과를 높였습니다.",
  "case-cctv-saha":
    "CCTV통합관제센터는 구민의 안전을 책임지는 공공 관제시설로, 노후화된 내부 환경을 전면 개선하는 구조환경개선공사를 진행하였습니다. 천장·바닥·벽체·전기·설비를 포함한 전체 리모델링을 통해 쾌적하고 기능적인 관제센터로 탈바꿈하였습니다.",
  "case-sinil":
    "신일화학 음성공장은 화학원료를 취급하는 공장으로 바닥 표면의 내화학성과 내마모성이 핵심 요건인 현장입니다. 노후 콘크리트 바닥에 에폭시 슬라이스 공법을 적용하여 라이닝 0.5MM + 코팅 0.1MM로 마감하였습니다.",
  "case-youngchemical":
    "영케미칼 양산공장은 의료용품 생산시설로 위생과 내구성이 최우선인 현장입니다. 기존 데코타일을 전면 제거하고 스키로더 연삭장비로 바닥 표면을 완전히 정리한 후, DNC에폭시 라이닝 3MM을 도포하여 유리와 같은 광택 표면을 완성하였습니다.",
  "case-deokcheon":
    "덕천아트빌라일원 옹벽경관개선사업은 노후화된 옹벽의 환경을 정비하여 쾌적한 도시 미관을 확보하기 위한 공사입니다. 기존 옹벽 표면을 고압세척으로 완전히 정리한 후 이형타일을 시공하고, 중성화방지도료로 마감하였습니다.",
  "case-jeilhwasung":
    "제일화성 온산공장은 화학공장 특성상 외벽 판넬 전반에 걸쳐 심각한 부식이 발생한 상태였습니다. 부식 구간은 글라인더로 완전 제거 후 보강작업을 실시하고, 멀티에폭시 프라이머 도장 후 중성화방지도료를 도장하여 내화학성과 내후성을 극대화하였습니다.",
  "case-jeonggwan":
    "정관보건지소 옥상·옥탑은 기존 우레탄 도막이 전면 들뜸 현상으로 방수 기능을 상실한 상태였습니다. 스키로더(밥캣)를 활용하여 기존 우레탄 도막을 완전 제거한 후, 우레탄 프라이머 → 우레탄 방수제 3MM → 우레탄 상도 순으로 시공하였습니다.",
  "case-bujeon":
    "부전인삼시장 공용화장실은 장기간 누수로 인해 슬라브층까지 손상이 진행된 상태였습니다. 기존 마감재와 슬라브를 전면 철거한 후 철저한 방수층 시공을 완료하였으며, 위생도기·타일·천장을 모두 교체하여 깔끔하게 리모델링하였습니다.",
  "case-00":
    "글로벌10003호는 노후화된 선체 외판 및 데크 전면의 도막 노화와 방청 보호를 위해 선박용 에폭시 전용 도장공사를 진행하였습니다. 데크는 PPG 에폭시 코팅제, 외판은 헴펠코리아 제품을 적용하여 해수염 환경에서의 내구성과 방식성을 극대화하였습니다.",
  "case-donggu":
    "동구노인복지관 분관은 노후된 드라이비트 외벽의 오염과 변색, 미세균열이 발생하여 외부 도장공사를 진행하였습니다. 드라이비트 외벽 전용 보수 및 도장 공법을 적용하여 균열 재발과 도막 박리를 최소화하였습니다.",
};

const categories = ["전체", ...Array.from(new Set(featuredCases.map((c) => c.category)))];

const collectionPageSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "시공사례 | 제이아이앤씨 JI&C",
  "description": "제이아이앤씨의 에폭시 바닥공사, 우레탄 방수, 도장, 실내건축, 석공사 등 다양한 시공 현장의 Before & After 사례 모음.",
  "url": import.meta.env.VITE_SITE_URL + "/portfolio",
  "isPartOf": {
    "@type": "WebSite",
    "name": "제이아이앤씨 JI&C",
    "url": import.meta.env.VITE_SITE_URL
  }
};

export default function PortfolioPage() {
  const [activeFilter, setActiveFilter] = useState("전체");

  const filtered =
    activeFilter === "전체"
      ? featuredCases
      : featuredCases.filter((c) => c.category === activeFilter);

  return (
    <>
      <JsonLd data={collectionPageSchema} />
      <div className="w-full bg-white text-navy-900">
        <Navbar />
        <main>
          {/* Hero */}
          <section className="relative w-full bg-navy-900 pt-24 pb-12 md:pt-36 md:pb-28">
            <div className="w-full px-5 md:px-12 lg:px-16">
              <div className="max-w-3xl">
                <div className="flex items-center gap-3 mb-4 md:mb-5">
                  <span className="w-8 h-px bg-point-500"></span>
                  <span className="text-point-400 text-xs font-semibold uppercase tracking-[0.3em]">
                    Portfolio
                  </span>
                </div>
                <h1 className="font-hero text-white text-3xl md:text-5xl lg:text-6xl leading-tight mb-4 md:mb-6">
                  시공사례
                </h1>
                <p className="text-white/60 text-sm md:text-lg leading-relaxed max-w-2xl">
                  JI&amp;C가 완성한 다양한 현장의 시공 전·후를 직접 확인하세요.
                  슬라이드를 좌우로 움직여 비포&amp;애프터를 비교할 수 있습니다.
                </p>
              </div>
            </div>
          </section>

          {/* Filter */}
          <section className="w-full py-5 md:py-10 bg-white border-b border-neutral-100 sticky top-[80px] z-30">
            <div className="w-full px-5 md:px-12 lg:px-16">
              <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveFilter(cat)}
                    className={`px-4 py-2 md:px-5 md:py-2.5 rounded-full text-xs md:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap shrink-0 ${
                      activeFilter === cat
                        ? "bg-navy-900 text-white"
                        : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Cases List */}
          <section className="w-full py-12 md:py-20 lg:py-24 bg-white">
            <div className="w-full px-5 md:px-12 lg:px-16">
              <div className="flex flex-col gap-14 md:gap-20">
                {filtered.map((c) => (
                  <article key={c.id} className="group">
                    {/* Case Header */}
                    <div className="mb-5 md:mb-6">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-point-50 text-point-700 text-xs font-semibold mb-3 md:mb-4">
                        <span className="w-1.5 h-1.5 rounded-full bg-point-500"></span>
                        {c.category}
                      </div>
                      <h2 className="font-hero text-navy-900 text-xl md:text-3xl leading-tight mb-3">
                        {c.title}
                      </h2>
                      <p className="text-neutral-600 text-sm md:text-base leading-relaxed max-w-3xl">
                        {caseDescriptions[c.id] || ""}
                      </p>
                    </div>

                    {/* Before/After Slider */}
                    <div className="rounded-2xl overflow-hidden">
                      <BeforeAfter before={c.before} after={c.after} />
                    </div>

                    {/* Meta Grid */}
                    <div className="mt-5 md:mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                      {[
                        { icon: "ri-calendar-line", label: "공사기간", value: c.period },
                        { icon: "ri-map-pin-line", label: "현장위치", value: c.location },
                        { icon: "ri-tools-line", label: "공법", value: c.method },
                        { icon: "ri-price-tag-3-line", label: "사용제품", value: c.product },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className="p-4 md:p-5 border border-neutral-200 rounded-xl hover:border-navy-800 transition-colors"
                        >
                          <div className="flex items-center gap-1.5 mb-2">
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className={`${item.icon} text-point-500 text-sm`}></i>
                            </div>
                            <div className="text-xs text-neutral-500 uppercase tracking-widest">
                              {item.label}
                            </div>
                          </div>
                          <div className="text-xs md:text-sm text-navy-900 font-medium leading-snug">
                            {item.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </article>
                ))}
              </div>

              {filtered.length === 0 && (
                <div className="text-center py-16 md:py-20">
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto flex items-center justify-center rounded-full bg-neutral-100 text-neutral-400 mb-4">
                    <i className="ri-inbox-line text-2xl md:text-3xl"></i>
                  </div>
                  <p className="text-neutral-500 text-sm">해당 카테고리의 시공사례가 없습니다.</p>
                </div>
              )}
            </div>
          </section>

          {/* CTA */}
          <section className="w-full py-14 md:py-20 lg:py-28 bg-navy-900">
            <div className="w-full px-5 md:px-12 lg:px-16 text-center">
              <h2 className="font-hero text-white text-2xl md:text-4xl leading-tight mb-3 md:mb-4">
                나만의 현장도 이렇게 바뀔 수 있습니다
              </h2>
              <p className="text-white/60 text-sm md:text-lg mb-8 md:mb-10 max-w-xl mx-auto leading-relaxed">
                지금 무료 현장 진단을 신청하고 최적의 시공 견적을 받아보세요.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/contact"
                  onClick={() => trackQuoteRequest()}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 bg-point-500 hover:bg-point-600 text-white text-base font-semibold rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  무료 견적문의 바로가기
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
                <a
                  href="tel:0517270477"
                  onClick={() => trackPhoneCall()}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/30 text-white text-base font-semibold rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line"></i>
                  </div>
                  051-727-0477
                </a>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}